import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { LoggerService, Document, Organization } from '../../shared/sdk';
import { DocumentService } from '../document.service';
import { Observable, Subject } from 'rxjs';
import { map, catchError, takeUntil, switchMap, filter } from 'rxjs/operators';
import { OrganizationService } from '../../organizations/organization.service';
import { ListSelectableClass } from '../../shared/base-list-selectable.class';
import { LanguageService } from '../../shared/services/language.service';
import { ClientService } from '../../clients/client.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { StorageService } from '../../shared/services/storage.service';

@Component({
  selector: 'app-active-documents',
  templateUrl: './active-documents.component.html',
  styleUrls: ['./active-documents.component.css'],
})
export class ActiveDocumentsComponent
  extends ListSelectableClass
  implements OnInit, OnDestroy
{
  @Input() incoming = false;
  @Input() title: string;
  @Input() cta: string;

  activeOrg: Organization;
  currentLang: string;
  documents$: Observable<Document[]>;
  lastPage = 1;
  locale: string;
  loading: any = {};
  order: string | string[] = 'dateDue';
  page = 1;
  savedLimit: number;
  totalItems = 0;
  totalItemsOnPage = 0;
  typeName: string;
  filters: FormGroup;
  showFilters: boolean;
  private lastFilter: string;
  private limitKey: string;

  private where: any = {
    type: { inq: ['invoice', 'credit-note'] },
    canceled: false,
    paidInFull: false,
    draft: false,
  };
  private onDestroy = new Subject<void>();

  constructor(
    public clientService: ClientService,
    private fb: FormBuilder,
    private documentService: DocumentService,
    private languageService: LanguageService,
    private log: LoggerService,
    private orgService: OrganizationService,
    private storageService: StorageService,
  ) {
    super();

    if (this.incoming) {
      this.order = [String(this.order), 'dateReceived'];
    }
  }

  ngOnInit(): void {
    this.limitKey = 'A:lk-' + (this.incoming ? 'i' : 'o');
    this.savedLimit = this.storageService.getItem(this.limitKey) || 5;

    this.filters = this.fb.group({
      limit: [this.savedLimit],
    });

    this.orgService.active
      .pipe(
        filter((org) => !!org),
        takeUntil(this.onDestroy),
      )
      .subscribe((o) => {
        this.activeOrg = o;
        this.get();
      });

    this.languageService.current.subscribe((l) => (this.currentLang = l));
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  get(force = false): void {
    this.log.log('ActiveDocuments: get');
    this.loading.data = true;
    const include: any = 'activities';
    const limit = this.filters.controls.limit.value;
    const filters = this.filters.getRawValue();
    const filterS = JSON.stringify(filters);

    if (filterS !== this.lastFilter) {
      this.page = 1;
    }
    this.lastFilter = filterS;
    const skip = (this.page - 1) * limit;

    let where = Object.assign({}, this.where);
    where = Object.assign(where, filters.filters);

    const query: any = {
      where,
      order: this.order,
      skip,
      limit,
      include,
    };

    if (this.incoming) {
      query.incoming = true;
      query.where.incoming = true;
    }

    this.documents$ = this.orgService.active.pipe(
      filter((org) => !!org),
      switchMap((activeOrg) =>
        this.documentService.get(activeOrg.id, query, force),
      ),
      catchError((err) => {
        this.totalItems = 0;
        this.totalItemsOnPage = 0;
        return err;
      }),
      map(({ body: documents, totalCount }) => {
        this.log.log('ActiveDocuments: get: loaded');
        this.totalItemsOnPage = documents?.length ?? 0;
        this.totalItems = totalCount ?? 0;
        this._items = documents;
        this.loading.data = false;
        return documents;
      }),
      takeUntil(this.onDestroy),
    );

    // Save limit
    if (limit !== this.savedLimit) {
      this.storageService.setItem(this.limitKey, limit);
      this.savedLimit = limit;
    }
  }

  isPast(document: Document): boolean {
    return this.documentService.isPast(document);
  }

  onAddPayment(): void {
    // TODO: Update stats
    this.get(true);
  }

  onCancel(document: Document): void {
    this.documentService.cancel(document.id).subscribe(() => {
      document.canceled = true;
      this.get(true);
    });
  }

  onDelete(document: Document): void {
    this.documentService.delete(document.id).subscribe(
      () => this.get(true),
      (err) => {},
    );
  }

  onDownloadSEPA(ids = this.selected.map((item) => item.id)): void {
    this.documentService.downloadSEPA(this.activeOrg, ids);
  }

  onPageChanged(page: number): void {
    if (this.lastPage === page) {
      return;
    }
    this.page = page;
    this.lastPage = this.page;
    this.get();
  }

  remainingDue(doc: Document): number {
    return this.documentService.getRemainingDue(doc);
  }
}
